import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Heart, Wind, Eye, Scan, Utensils, Network, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import HumanBody from '../components/HumanBody';
import ProjectCard from '../components/ProjectCard';
import ParticleBackground from '../components/ParticleBackground';
import BackToTop from '../components/BackToTop';
import ScrollReveal from '../components/ScrollReveal';
import Testimonials from '../components/Testimonials';
import Partners from '../components/Partners';
import SEO from '../components/SEO';
import MorphingGradient from '../components/MorphingGradient';
import AmbientBackground from '../components/AmbientBackground';
import AnimatedIcon from '../components/AnimatedIcon';
import AnimatedLogoBackground from '../components/AnimatedLogoBackground';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';

const Home = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const projects = [
    {
      id: 'cardio',
      title: 'Yamai Cardio',
      description: 'An intelligent system combining echocardiography and ECG to detect early wall-motion abnormalities using explainable AI.',
      status: 'In R&D',
      icon: Heart,
      color: '#14B8A6',
      path: '/projects/cardio',
    },
    {
      id: 'neuro',
      title: 'Yamai Neuro',
      description: 'AI-powered analysis of brain signals and neural patterns for early detection of neurological conditions.',
      status: 'In R&D',
      icon: Brain,
      color: '#7C3AED',
      path: '/projects/neuro',
    },
    {
      id: 'derma',
      title: 'Yamai Derma',
      description: 'Computer vision-based dermal tissue analysis for early skin condition detection and monitoring.',
      status: 'In R&D',
      icon: Scan,
      color: '#EC4899',
      path: '/projects/derma',
    },
    {
      id: 'pulmo',
      title: 'Yamai Pulmo',
      description: 'Smart respiratory and lung function analysis for comprehensive pulmonary health assessment.',
      status: 'In R&D',
      icon: Wind,
      color: '#3B82F6',
      path: '/projects/pulmo',
    },
    {
      id: 'vision',
      title: 'Yamai Vision',
      description: 'Retinal pattern detection and analysis for early diagnosis of eye-related diseases.',
      status: 'In R&D',
      icon: Eye,
      color: '#8B5CF6',
      path: '/projects/vision',
    },
    {
      id: 'nutriscan',
      title: 'Yamai NutriScan',
      description: 'Metabolic and nutritional signal analysis for personalized health insights.',
      status: 'In R&D',
      icon: Utensils,
      color: '#10B981',
      path: '/projects/nutriscan',
    },
    {
      id: 'healthnet',
      title: 'Yamai HealthNet',
      description: 'A unified diagnostic ecosystem integrating multiple organ-based AI models into a holistic health intelligence platform.',
      status: 'Vision',
      icon: Network,
      color: '#F59E0B',
      path: '/projects/healthnet',
    },
  ];

  const milestones = [
    { year: 'Q4 2025 - Q2 2026', title: 'Cardio Prototype', description: 'Echo + ECG AI model development' },
    { year: 'Q3 2026 - Q2 2027', title: 'Clinical Validation', description: 'Netherlands & Belgium trials' },
    { year: 'Q3 2027 - Q4 2028', title: 'MDR/CE Certification', description: 'Regulatory approval process' },
    { year: '2029 - 2030', title: 'Market Introduction', description: 'Commercial launch in EU' },
    { year: '2031 - 2035', title: 'HealthNet Integration', description: 'Unified multi-organ platform' },
  ];

  const differentiators = [
    {
      title: 'Explainable AI',
      description: 'Transparent algorithms that show doctors exactly how decisions are made.',
    },
    {
      title: 'Multi-modal Integration',
      description: 'Combining multiple data sources for comprehensive diagnostic insights.',
    },
    {
      title: 'Clinically Validated',
      description: 'Rigorously tested with medical professionals and real-world data.',
    },
    {
      title: 'Ethically Designed',
      description: 'GDPR-compliant, bias-aware, and built with patient privacy first.',
    },
  ];

  return (
    <div className="min-h-screen relative">
      <SEO 
        title={t('home.hero.title')}
        description={t('home.hero.subtitle')}
        keywords="medical AI, explainable AI, healthcare technology, diagnostic AI, cardiology AI, neurology AI, dermatology AI"
      />
      
      {/* NEW: Morphing Gradient Background */}
      <MorphingGradient />
      
      {/* NEW: Ambient Background Motion */}
      <AmbientBackground />
      
      {/* NEW: Animated Logo Background */}
      <AnimatedLogoBackground />
      
      {/* Particle Background */}
      <ParticleBackground />
      
      {/* Back to Top Button */}
      <BackToTop />
      
      {/* Hero Section - Grid Layout */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Interactive Human Body on Desktop, Text on Mobile */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="order-2 lg:order-1"
            >
              <div className="hidden lg:block float-animation">
                <HumanBody />
              </div>
              <div className="block lg:hidden">{/* Text will show here on mobile */}</div>
            </motion.div>

            {/* Right: Text Content on Desktop, Projects on Mobile */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="order-1 lg:order-2"
            >
              {/* Badge with shimmer */}
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yamai-cyan-500/20 to-yamai-purple-500/20 backdrop-blur-sm rounded-full px-6 py-3 mb-8 border border-yamai-cyan-500/30 animate-pulse-glow">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yamai-cyan-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-yamai-cyan-500"></span>
                </span>
                <span className="text-sm font-medium text-gradient-animate">
                  Founded 2025 • Breda, Netherlands
                </span>
              </div>

              {/* Hero Title with Gradient Animation */}
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                <span className="block text-gradient-animate">
                  Deeper Vision. Smarter Health.
                </span>
              </h1>

              <p className="text-xl md:text-2xl text-[#8A90A0] mb-12 leading-relaxed">
                We build explainable medical AI systems that combine cutting-edge deep learning with clinical precision. 
                Our research focuses on multi-organ diagnostic intelligence.
              </p>

              {/* CTA Buttons with Enhanced Effects */}
              <div className="flex flex-wrap gap-4">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    size="lg"
                    onClick={() => navigate('/projects')}
                    className="relative group bg-gradient-to-r from-yamai-cyan-500 to-yamai-purple-500 text-white px-8 py-6 rounded-xl text-lg font-semibold overflow-hidden hover-glow-cyan"
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      Explore Projects
                      <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-yamai-purple-500 to-yamai-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </Button>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate('/contact')}
                    className="glass-effect border-yamai-cyan-500/50 text-white px-8 py-6 rounded-xl text-lg hover-glow-purple"
                  >
                    Contact Us
                  </Button>
                </motion.div>
              </div>
            </motion.div>

            {/* Show Projects on Mobile only */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="block lg:hidden order-2"
            >
              <div className="float-animation">
                <HumanBody />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Preview Section - Transparent Background */}
      <section className="py-24 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="bg-gradient-to-r from-yamai-cyan-400 to-yamai-purple-400 bg-clip-text text-transparent">
                  {t('home.foundation.title')}
                </span>
              </h2>
              <p className="text-[#8A90A0] text-lg max-w-2xl mx-auto">
                {t('home.foundation.subtitle')}
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8">
            <ScrollReveal delay={0}>
              <motion.div
                whileHover={{ scale: 1.05, y: -5 }}
                className="glass-effect rounded-2xl p-8 border border-yamai-purple-500/30 hover:border-yamai-purple-500/60 transition-all duration-300 hover-glow-purple"
              >
                <div className="w-12 h-12 rounded-lg bg-yamai-purple-500/20 flex items-center justify-center mb-4">
                  <AnimatedIcon icon={Brain} color="#9C27B0" size={24} />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-3">{t('home.foundation.mission')}</h3>
                <p className="text-[#8A90A0] leading-relaxed">
                  {t('home.foundation.missionText')}
                </p>
              </motion.div>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              <motion.div
                whileHover={{ scale: 1.05, y: -5 }}
                className="glass-effect rounded-2xl p-8 border border-yamai-cyan-500/30 hover:border-yamai-cyan-500/60 transition-all duration-300 hover-glow-cyan"
              >
                <div className="w-12 h-12 rounded-lg bg-yamai-cyan-500/20 flex items-center justify-center mb-4">
                  <AnimatedIcon icon={Eye} color="#00BCD4" size={24} />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-3">{t('home.foundation.vision')}</h3>
                <p className="text-[#8A90A0] leading-relaxed">
                  {t('home.foundation.visionText')}
                </p>
              </motion.div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <motion.div
                whileHover={{ scale: 1.05, y: -5 }}
                className="glass-effect rounded-2xl p-8 border border-yamai-cyan-500/30 hover:border-yamai-cyan-500/60 transition-all duration-300 hover-glow-cyan"
              >
                <div className="relative w-16 h-16 rounded-xl bg-gradient-to-r from-yamai-cyan-500/20 to-yamai-purple-500/20 flex items-center justify-center mb-4 animate-liquid-morph">
                  <AnimatedIcon icon={Heart} color="#00BCD4" size={28} />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-3">{t('home.foundation.values')}</h3>
                <p className="text-[#8A90A0] leading-relaxed">
                  {t('home.foundation.valuesText')}
                </p>
              </motion.div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Projects Grid - Transparent Background */}
      <section className="py-24 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              <span className="text-gradient-animate">
                {t('home.ourProjects')}
              </span>
            </h2>
            <p className="text-[#8A90A0] text-lg max-w-2xl mx-auto">
              {t('home.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="transition-all duration-300"
              >
                <ProjectCard project={project} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline/Roadmap */}
      <section className="py-24 bg-[#0B1220]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-yamai-cyan-400 to-yamai-purple-400 bg-clip-text text-transparent">
                {t('home.roadmap.title')}
              </span>
            </h2>
            <p className="text-[#8A90A0] text-lg max-w-2xl mx-auto">
              {t('home.roadmap.subtitle')}
            </p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-yamai-purple-500 via-yamai-cyan-500 to-yamai-purple-400 opacity-30" />

            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-8 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                    <div className="inline-block bg-[#1F2A3A]/50 backdrop-blur-sm rounded-xl p-6 border border-[#7C3AED]/30">
                      <p className="text-[#7C3AED] font-mono text-sm mb-2">{milestone.year}</p>
                      <h3 className="text-xl font-semibold text-white mb-2">{milestone.title}</h3>
                      <p className="text-[#8A90A0] text-sm">{milestone.description}</p>
                    </div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="w-4 h-4 rounded-full bg-[#7C3AED] border-4 border-[#0B1220] shadow-lg shadow-[#7C3AED]/50" />
                  </div>
                  
                  <div className="flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Yamai */}
      <section className="py-24 bg-gradient-to-b from-[#0B1220] to-[#2B1E4A]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Why Yamai</h2>
            <p className="text-[#8A90A0] text-lg max-w-2xl mx-auto">
              What sets us apart in the medical AI landscape
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {differentiators.map((item, index) => (
              <div
                key={index}
                className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-xl p-6 border border-[#1F2A3A] hover:border-[#7C3AED]/50 transition-all duration-300 hover:transform hover:scale-105"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#14B8A6] flex items-center justify-center mb-4 text-white font-bold text-xl">
                  {index + 1}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-[#8A90A0] text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Looking for Partners Section */}
      <section className="py-24 bg-gradient-to-b from-[#0B1220] to-[#2B1E4A]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                <span className="bg-gradient-to-r from-[#7C3AED] to-[#14B8A6] bg-clip-text text-transparent">Looking for</span> Partners
              </h2>
              <p className="text-[#8A90A0] text-lg max-w-3xl mx-auto">
                We seek to collaborate with leading healthcare institutions and research organizations to build the future of medical artificial intelligence
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Hospitals',
                icon: '🏥',
                description: 'For clinical trials and system validation',
              },
              {
                title: 'Research Centers',
                icon: '🔬',
                description: 'For collaboration in algorithm and model development',
              },
              {
                title: 'Universities',
                icon: '🎓',
                description: 'For academic research and technology development',
              },
              {
                title: 'Investors',
                icon: '💼',
                description: 'To support growth and market expansion',
              },
            ].map((partner, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-2xl p-8 border border-[#1F2A3A] hover:border-[#7C3AED]/50 transition-all duration-300 hover:transform hover:scale-105 text-center">
                  <div className="text-5xl mb-4">{partner.icon}</div>
                  <h3 className="text-xl font-semibold text-white mb-3">{partner.title}</h3>
                  <p className="text-[#8A90A0] text-sm leading-relaxed">{partner.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Support Us Section */}
      <section className="py-24 bg-[#0B1220] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#7C3AED]/10 via-transparent to-[#14B8A6]/10" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Support <span className="bg-gradient-to-r from-[#7C3AED] to-[#14B8A6] bg-clip-text text-transparent">Our Project</span>
              </h2>
              <p className="text-[#8A90A0] text-lg max-w-2xl mx-auto mb-8">
                Help us build the future of ethical and transparent medical artificial intelligence
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-6">
            <ScrollReveal delay={0}>
              <div className="bg-gradient-to-br from-[#7C3AED]/20 to-[#7C3AED]/5 backdrop-blur-sm rounded-xl p-6 border border-[#7C3AED]/30 hover:transform hover:scale-105 transition-all">
                <div className="text-4xl mb-4">🤝</div>
                <h3 className="text-xl font-semibold text-white mb-3">Partnership</h3>
                <p className="text-[#8A90A0] text-sm">Collaborate with us in research and development</p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              <div className="bg-gradient-to-br from-[#14B8A6]/20 to-[#14B8A6]/5 backdrop-blur-sm rounded-xl p-6 border border-[#14B8A6]/30 hover:transform hover:scale-105 transition-all">
                <div className="text-4xl mb-4">💰</div>
                <h3 className="text-xl font-semibold text-white mb-3">Investment</h3>
                <p className="text-[#8A90A0] text-sm">Invest in the future of healthcare</p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="bg-gradient-to-br from-[#10B981]/20 to-[#10B981]/5 backdrop-blur-sm rounded-xl p-6 border border-[#10B981]/30 hover:transform hover:scale-105 transition-all">
                <div className="text-4xl mb-4">🎓</div>
                <h3 className="text-xl font-semibold text-white mb-3">Scientific Research</h3>
                <p className="text-[#8A90A0] text-sm">Join our research team</p>
              </div>
            </ScrollReveal>
          </div>

          <div className="text-center mt-12">
            <Button
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-gradient-to-r from-[#7C3AED] to-[#14B8A6] hover:opacity-90 text-white px-10 py-6 text-lg rounded-xl shadow-lg transition-all duration-300 hover:scale-105"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0B1220]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Partner with us to build a transparent,
              <br />
              <span className="bg-gradient-to-r from-[#7C3AED] to-[#14B8A6] bg-clip-text text-transparent">
                human-centered AI future
              </span>
            </h2>
            <p className="text-[#8A90A0] text-lg mb-8 max-w-2xl mx-auto">
              Let's shape the future of ethical medical AI — together.
            </p>
            <Button
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-gradient-to-r from-[#7C3AED] to-[#14B8A6] hover:opacity-90 text-white px-10 py-6 text-lg rounded-xl shadow-lg transition-all duration-300 hover:scale-105"
            >
              Get in Touch
            </Button>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default Home;